/** @jsxImportSource @emotion/react */
import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import useEvent from 'react-use-event-hook';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { Workbook } from '@grapecity/wijmo.xlsx';
import { CellMaker } from '@grapecity/wijmo.grid.cellmaker';
import { addClass, toggleClass } from '@grapecity/wijmo';
import { CellType } from '@grapecity/wijmo.grid';
import { Button, Checkbox } from '@mui/material';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import PropTypes from 'prop-types';
import CustomGrid from 'components/grids/CustomGrid';
import { hasRole } from 'utils/SessionUtil';
import { useMessageBar } from 'hooks/useMessageBar';
import useSessionStore from 'stores/useSessionStore';
import { getCommonCodeNames, getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import { getExcelFileName } from 'utils/ExcelUtil';
import { tabs } from 'components/layouts/Tab';
import { IconButton } from 'components/buttons/IconSVG';
import { SetupScheduleCondition } from 'models/setup/SetupSchedule';
import { st } from 'components/inputs/CustomInput';
import {
  findScheduleStatusDetail,
  createRequestSetupResultRequest,
  saveScheduleResult,
} from 'apis/setup/SetupSchedule';
import { GridProgressCellTemplate } from 'components/grids/GridProgressCellRenderer';
import { PageMode } from 'models/common/Common';
import SetupEquipmentScheduleChartPopup from '../popup/SetupEquipmentScheduleChartPopup';
import { CommonUtil } from 'utils/CommonUtil';
import ExcelUploadButton from 'components/buttons/ExcelUploadButton';
import { getPlanProgressRate, getProgressRate, getProgressRateTitle } from 'utils/SetupUtil';
import ProjectIssueCreatePage from '../issue/ProjectIssueRegisterPage';
import { GlobalBtnGroup, StatusDot, SubTitleGroup } from 'components/layouts/ContentLayout';
import GridSettingButtons from 'components/grids/GridSettingButtons';
import { checkAvailableProjectManager } from 'apis/setup/SetupProject';
import SetupResultCompleteRequestPopup from '../popup/SetupResultCompleteRequestPopup';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import { SetupStep } from 'models/setup/SetupStep';
import { findProjectStepBySpTpCd, findProjectStepType } from 'apis/setup/SetupStep';

/**
 * @desc 설비셋업관리 > 프로젝트 셋업관리 > 프로젝트 일정 등록/조회 > [일정현황 상세] 탭 내용
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.10.24   jshwang12       신규 생성
 * 2025.11.27   jshwang12       작업일보에서 사용하는 엑셀템플릿다운로드/업로드 버튼 위치 이동 (-> 일정상세현황 그리드 상단으로 위치 이동)
 * 2026.01.19   jshwang12       실적완료요청/승인 버튼 위치 이동 (-> 일정현황상세 탭)
 * 2026.01.26   jshwang12       작업일보 > 일정상세현황탭 그리드를 더 늘려서 스크롤 없애도록 기능 개선
 * 2026.01.26   jshwang12       작업일보 > 변경 내용만 보기 추가 (Default 체크), 그리드 셀 강조테투리 추가
 * 2026.01.27   jshwang12       tab 배경색 추가 (데이터 있는 경우 미리 탭으로 구분하기 위함)
 * 2026.01.27   jshwang12       작업일보 > 실적불러오기 기능 추가
 * 2026.01.30   jshwang12       FAB-In 변경비율 확인 대상에서 제거 (화면에서만 일단 수정하고 추후 패키지 수정 후 재적용 필요), FAB-IN 실적 안내문구 추가
 * 2026.04.14   jshwang12       작업일보 > 일정현황상세 > 현황 클릭 시 해당 설비와 단계를 기본값으로 하는 이슈등록팝업을 노출
 * 2026.05.21   jihwan89        그리드 핀 고정 설비명까지 추가
 * 2026.05.26   jshwang12       지연 상태 DLY_WRN 추가 (지연(30▼)), 진행상태 label명 변경(작업 시작전 -> 시작전, 실적승인완료 -> 완료, 지연 -> 지연(30▲))
 * 2026.07.01   jshwang12       설비단계자동설정 기능 적용 (공통코드에서 테이블로 변경)
 * 2026.07.08   jihwan89        셋업일보 처음 작성시 처음 입력(현황 0) 하면 일보작성일이 실제 시작일로 들어가도록 수정
 */
interface Props {
  type?: 'SCHD' | 'DAILY_REPORT';
  isReadOnly?: boolean;
  showTemplateDownload?: boolean;
  showExcelUpload?: boolean;
  showReloadResult?: boolean;
  onTemplateDownload?: () => void;
  onExcelUploadPreCheck?: () => Promise<boolean>;
  onExcelUploadCallback?: (file) => void;
  onReloadResult?: () => void;
  onOpenCreateIssuePopup?: (prjSpCd, tgtEqpId, eqpId, eqpNm, eqclId) => void; // [26.04.14] 단계 이슈 팝업 추가
  crtnDt?: string;
}

const ScheduleStatusDetailContent = (
  { type = 'SCHD', isReadOnly = true, ...props }: Props,
  ref
) => {
  const gridRef = useRef<any>();
  const gridUiRef = useRef<any>();
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { ensolYn } = useSessionStore();
  const originRowData = useRef<any>();
  const [rowData, setRowData] = useState<any>();
  const [condition, setCondition] = useState<SetupScheduleCondition>();
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);
  const [code, setCode] = useState<any>();
  const [isOpenScheduleChart, setOpenScheduleChart] = useState<boolean>(false);
  const [scheduleChartCondition, setScheduleChartCondition] = useState<any>();
  const [openIssuePopup, setOpenIssuePopup] = useState<boolean>(false);
  const [issuePopupCondition, setIssuePopupCondition] = useState<any>();
  const [isChangeColumn, setIsChangeColumn] = useState<boolean>(false);
  const [hasPrjMgrAuth, setHasPrjMgrAuth] = useState<boolean>(false);
  const [isOpenResultRequest, setOpenResultRequest] = useState<boolean>(false);
  const [resultRequestCondition, setResultRequestCondition] = useState<any>();
  const [gridHeight, setGridHeight] = useState<number>();

  useImperativeHandle(ref, () => ({
    /**
     * @param params {
     *     elmPrjId: 프로젝트ID (필수)
     *     , excpId: 현력사ID
     *     , wkId: 작업Id (작업일보에서 접근하는 경우)
     * }
     */
    search: (params) => {
      const p = {
        ...params,
        delayYn: condition?.delayYn || 'N',
        changeYn: condition?.changeYn || (type === 'DAILY_REPORT' && isReadOnly ? 'Y' : 'N'), // [26.01.26] 변경내용만 보기 기본값 추가
      };
      setCondition(p);
      handleSearch(p);
    },
    validate: () => {
      return true;
    },
    save: async () => {
      await saveScheduleResult(
        condition?.elmPrjId,
        condition?.excpId,
        condition?.wkId,
        props?.crtnDt,
        rowData
      ).then((res) => {
        if (res?.successOrNot === 'Y') {
          handleSearch(condition);
        } else {
          openMessageBar({
            type: 'error',
            content:
              res.data && typeof res.data === 'string'
                ? res.data
                : t(
                    'setup.label.실적 저장 중 오류가 발생했습니다.',
                    '실적 저장 중 오류가 발생했습니다.'
                  ),
          });
        }
      });
    },
  }));

  const tabMenus = useMemo(() => {
    return (code?.spTpCd || []).reduce(
      (acc, cur) => [
        ...acc,
        {
          id: cur.spTpCd,
          name: cur.spTpNm,
        },
      ],
      []
    );
  }, [code?.spTpCd]);

  useEffect(() => {
    getCommonCodes();
  }, []);

  useEffect(() => {
    if (condition?.elmPrjId) {
      if (hasRole('FA_MANAGER')) {
        setHasPrjMgrAuth(true);
      } else {
        checkAvailableProjectManager(condition?.elmPrjId, 'ALL').then((res) =>
          setHasPrjMgrAuth(res)
        );
      }
      getCommonCodes();
    } else {
      setHasPrjMgrAuth(false);
    }
  }, [condition?.elmPrjId]);

  useEffect(() => {
    if (type === 'DAILY_REPORT') {
      setCondition((prev) => ({
        ...prev,
        changeYn: isReadOnly ? 'Y' : 'N', // 수정인 경우 변경내용만 보기 해제
      }));
    }
  }, [type, isReadOnly]);

  useEffect(() => {
    handleSearch(condition);
  }, [condition?.elmPrjId, condition?.excpId, code?.spTpCd]);

  const filterRowData = useMemo(() => {
    const spTpCd = tabMenus?.[currentTabIndex]?.id;
    if (rowData && spTpCd) {
      if (Object.keys(rowData || {}).filter((key) => key === spTpCd)) {
        return (rowData[spTpCd] || []).filter((o) => {
          if (condition?.delayYn === 'Y' || condition?.changeYn === 'Y') {
            const delay =
              Object.keys(o).filter(
                (key) => key.endsWith('PRJ_SP_STAT') && ['DLY', 'DLY_WRN'].includes(o[key] || '')
              ).length > 0;

            const change =
              Object.keys(o).filter((key) => {
                const cd = key.match(/^'(.*)'_INCR_RT$/)?.[1];
                return (
                  key.endsWith('_INCR_RT') &&
                  !isNaN(Number(o[key])) &&
                  Number(o[key]) != 0 &&
                  cd !== 'T040'
                ); // [26.01.30] FAB-In 변경비율 확인 대상에서 제거 (화면에서만 일단 수정하고 추후 패키지 수정 후 재적용 필요)
              }).length > 0;
            if (condition?.delayYn === 'Y' && condition?.changeYn === 'Y') {
              return delay && change;
            }
            if (condition?.delayYn === 'Y') return delay;
            if (condition?.changeYn === 'Y') return change;
          }
          return true;
        });
      }
    }
    return [];
  }, [rowData, currentTabIndex, condition?.delayYn, condition?.changeYn]);

  const getCommonCodes = async () => {
    if (condition?.elmPrjId) {
      const spTpCds: SetupStep[] = await findProjectStepType(condition?.elmPrjId);
      const prjSpCds = { spTpCd: spTpCds } as any;
      if ((spTpCds || []).length) {
        await Promise.all(
          spTpCds.map(async (o) => {
            if (o.spTpCd) {
              prjSpCds[o.spTpCd] = await findProjectStepBySpTpCd({
                spTpCd: o.spTpCd,
                elmPrjId: condition?.elmPrjId,
                autoCalYn: 'N', // 자동계산단계 제외 (=일정상세표시여부 Y 항목만 노출)
              });
            }
          })
        );
      }
      setCode({
        ...prjSpCds,
      });
    }
  };

  const handleSearch = async (params) => {
    if (type === 'DAILY_REPORT' && !params?.wkId) {
      setRowData({});
      originRowData.current = cloneDeep({});
    } else if (params?.elmPrjId && tabMenus?.[currentTabIndex]?.id) {
      const result = await findScheduleStatusDetail(
        params?.elmPrjId,
        undefined, // tabMenus?.[currentTabIndex]?.id,
        params?.excpId,
        params?.wkId
      );
      setRowData(result);
      originRowData.current = cloneDeep(result);
    } else {
      setRowData({});
      originRowData.current = cloneDeep({});
    }
  };

  const convertLayoutDefinition = (prjSpCd: SetupStep[]) => {
    const stateCodes = {
      PROG: 'ing',
      DLY_WRN: 'warning', // [26.05.26] 지연 상태 DLY_WRN 추가
      DLY: 'delay',
      CPLT: 'done',
      PLAN: 'none',
    };

    const getProgRtTag = (params) => {
      const statKey = params?.col?.binding?.replace('_PROG_RT', '_PRJ_SP_STAT');
      const crsKey = params?.col?.binding?.replace('_PROG_RT', '_PRJ_SP_CRS');
      const isuKey = params?.col?.binding?.replace('_PROG_RT', '_ISU_ID');
      const planStDtKey = params?.col?.binding?.replace('_PROG_RT', '_PLAN_ST_DT');
      const planEndDtKey = params?.col?.binding?.replace('_PROG_RT', '_PLAN_END_DT');
      const incrRtKey = params?.col?.binding?.replace('_PROG_RT', '_INCR_RT');
      const isuStatKey = params?.col?.binding?.replace('_PROG_RT', '_ISU_STAT_CD');

      if (!statKey || !crsKey) return '';
      const prjSpStat = params.item[statKey];
      if (!prjSpStat) return '';
      let crs = '';
      if ('DLY' === prjSpStat || 'DLY_WRN' === prjSpStat) {
        crs = params.item[crsKey] || '';
      }
      const issue = isuKey && params.item[isuKey];
      const issueStat = isuStatKey && params.item[isuStatKey];
      const state = stateCodes[prjSpStat] || 'none';
      let title = '';
      if (planStDtKey && planEndDtKey) {
        title = `${t('setup.label.계획', '계획')}: ${getPlanProgressRate(
          params.item[planStDtKey],
          params.item[planEndDtKey]
        )}<br/>${t('setup.label.실적', '실적')}: ${getProgressRateTitle(
          params.value || 0,
          Number(crs || 0)
        )}`;
        if (incrRtKey) {
          const incrRt = Number(params.item[incrRtKey]);
          const cd = incrRtKey.match(/^'(.*)'_INCR_RT$/)?.[1];
          // [26.01.30] FAB-In 변경비율 확인 대상에서 제거 (화면에서만 일단 수정하고 추후 패키지 수정 후 재적용 필요)
          if (!isNaN(incrRt) && incrRt != 0 && cd !== 'T040') {
            title += `<br/>${t('setup.label.변경 비율', '변경 비율')}: ${getProgressRate(incrRt)}`;
          }
        }
      }
      return GridProgressCellTemplate(
        state,
        params.value || 0,
        Number(crs || 0),
        title,
        !!issue,
        issueStat
      );
    };

    // const prjSpCd = tabMenus?.[currentTabIndex]?.id && code?.[tabMenus?.[currentTabIndex]?.id];
    const prjSpLayout = (prjSpCd || []).reduce(
      (acc, cur, idx) => [
        ...acc,
        {
          header: cur.prjSpNm,
          columns: [
            {
              header: String(t('setup.label.계획 시작일', '계획 시작일')),
              binding: `'${cur.prjSpCd}'_PLAN_ST_DT`,
              minWidth: 60,
              width: 87,
              align: 'center',
              isReadOnly: true,
            },
            {
              header: String(t('setup.label.계획 종료일', '계획 종료일')),
              binding: `'${cur.prjSpCd}'_PLAN_END_DT`,
              minWidth: 60,
              width: 87,
              align: 'center',
              isReadOnly: true,
            },
            {
              header: String(t('setup.label.현황', '현황')),
              binding: `'${cur.prjSpCd}'_PROG_RT`,
              minWidth: 100,
              width: 150,
              align: 'left',
              format: 'g',
              isRequired: false,
              isReadOnly: isReadOnly,
              cssClass: isReadOnly ? 'WijmoIconButton' : '',
              cellTemplate: isReadOnly ? getProgRtTag : undefined,
            },
          ],
        },
      ],
      [] as any[]
    );

    return [
      {
        header: String(t('com.label.상태', '상태')),
        binding: 'crudKey',
        minWidth: 40,
        width: 40,
        isReadOnly: true,
        visible: !isReadOnly,
        align: 'center',
        cellTemplate: GridStatusCellTemplate,
      },
      {
        header: String(t('setup.label.공정', '공정')),
        binding: 'PRDN_PROC_CD_NM',
        minWidth: 70,
        width: 110,
        align: 'center',
        isReadOnly: true,
      },
      {
        header: String(t('com.label.설비군', '설비군')),
        binding: 'EQCL_ID',
        minWidth: 70,
        width: 80,
        align: 'center',
        isReadOnly: true,
      },
      {
        header: String(t('setup.label.양/음극', '양/음극')),
        binding: 'ELTR_TP_CD_NM',
        minWidth: 70,
        width: 90,
        align: 'center',
        isReadOnly: true,
      },
      {
        header: String(t('setup.label.호기', '호기')),
        binding: 'EQP_LN_ID',
        minWidth: 80,
        width: 80,
        align: 'center',
        isReadOnly: true,
      },
      {
        header: String(t('setup.label.M-CODE', 'M-CODE')),
        binding: 'EQP_ID',
        minWidth: 80,
        width: 110,
        align: 'center',
        isReadOnly: true,
        cellTemplate: CellMaker.makeLink({
          click: (e, ctx) => {
            if (condition?.elmPrjId && ctx.item?.['TGT_EQP_ID']) {
              setOpenScheduleChart(true);
              setScheduleChartCondition({
                elmPrjId: condition?.elmPrjId,
                tgtEqpId: ctx.item?.['TGT_EQP_ID'],
                targetData: ctx.item,
              });
            }
          },
        }),
      },
      {
        header: String(t('setup.label.설비명', '설비명')),
        binding: 'EQP_NM',
        minWidth: 80,
        width: 140,
        align: 'left',
        isReadOnly: true,
      },
      {
        header: String(t('setup.label.협력업체명', '협력업체명')),
        binding: 'EXCP_NM',
        minWidth: 80,
        width: 100,
        align: 'left',
        isReadOnly: true,
      },
      {
        header: String(t('setup.label.타겟일정', '타겟일정')),
        binding: 'TGT_PLAN_END_DT',
        minWidth: 85,
        width: '*',
        align: 'center',
        isReadOnly: true,
      },
      ...prjSpLayout,
    ];
  };

  const layoutDefinition = useMemo(() => {
    const prjSpCd = tabMenus?.[currentTabIndex]?.id && code?.[tabMenus?.[currentTabIndex]?.id];
    return convertLayoutDefinition(prjSpCd);
  }, [code, currentTabIndex, isChangeColumn, condition, isReadOnly]);

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;
      // [26.01.26] 값이 변경된 경우 셀 강조테투리 추가
      if (binding.endsWith('_PROG_RT')) {
        const cd = binding.match(/^'(.*)'_PROG_RT$/)?.[1];
        if (isReadOnly) {
          const key = binding.replace('_PROG_RT', '_INCR_RT');
          const incrRt = Number(item[key]);
          // [26.01.30] FAB-In 변경비율 확인 대상에서 제거 (화면에서만 일단 수정하고 추후 패키지 수정 후 재적용 필요)
          if (!isNaN(incrRt) && item[key] != 0 && cd !== 'T040') {
            addClass(cell, 'innerBorder');
          }
        } else {
          // 완료된 경우 실적 변경 불가
          const cpltYnKey = binding.replace('_PROG_RT', '_CPLT_YN');
          cell.ariaReadOnly = cpltYnKey && item?.[cpltYnKey] === 'Y';
          // FAB-IN 실적입력 불가
          if (cd === 'T040') {
            cell.ariaReadOnly = true;
          }
          const spTpCd = tabMenus?.[currentTabIndex]?.id;
          if (spTpCd) {
            const origin = originRowData.current?.[spTpCd] || [];
            if (origin.length > row) {
              toggleClass(
                cell,
                'innerBorder',
                !isEqual(origin[row]?.[binding] || 0, item[binding] || 0)
              );
            }
          }
        }
      }
    }
  });

  const onInitialized = (grid) => {
    gridRef.current = grid;
    grid.itemFormatter = onItemFormatter;

    grid.hostElement.addEventListener('click', (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;

      const item = grid.rows[ht.row].dataItem;
      const binding = grid.columns[ht.col].binding;

      if (ht.panel === grid.cells) {
        // 현황
        if (binding.endsWith('_PROG_RT')) {
          if (e.target instanceof HTMLButtonElement) {
            if (e.target.classList.contains('issue')) {
              // 이슈 버튼 클릭 시 해당 설비/단계의 가장 최근 이슈로 이동
              const isuKey = binding?.replace('_PROG_RT', '_ISU_ID');
              const isuId = isuKey && item[isuKey];
              if (isuId) {
                setIssuePopupCondition({
                  isuId: isuId,
                });
                setOpenIssuePopup(true);
              }
            }
          }
        }
      }
    });

    grid.hostElement.addEventListener('dblclick', (e) => {
      // [26.04.14] 작업일보 > 일정현황상세 > 현황 더블클릭 시 해당 설비와 단계를 기본값으로 하는 이슈등록팝업을 노출
      if (type !== 'DAILY_REPORT') return;
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;

      const binding = grid.columns[ht.col].binding;
      if (ht.panel === grid.cells) {
        const item = grid.rows[ht.row].dataItem;
        // 현황
        if (binding.endsWith('_PROG_RT')) {
          const cd = binding.match(/^'(.*)'_PROG_RT$/)?.[1];
          props?.onOpenCreateIssuePopup?.(
            cd,
            item['TGT_EQP_ID'],
            item['EQP_ID'],
            item['EQP_NM'],
            item['EQCL_ID']
          );
        }
      }
    });

    setIsChangeColumn(true);
    gridRef.current?.refresh();
  };

  const handleDownloadExcel = async () => {
    const workbook = new Workbook();
    for (const spTpCd of code?.spTpCd || []) {
      const ws = CommonUtil.createWijmoSheet(
        spTpCd.spTpNm,
        convertLayoutDefinition(code?.[spTpCd.spTpCd]),
        rowData[spTpCd.spTpCd]
      );
      workbook.sheets.push(ws);
    }
    workbook.saveAsync(getExcelFileName(t('setup.label.일정현황 상세', '일정현황 상세')));
  };

  const includePin = [
    'PRDN_PROC_CD_NM',
    'EQCL_ID',
    'ELTR_TP_CD_NM',
    'EQP_LN_ID',
    'EQP_ID',
    'EQP_NM',
    'EXCP_NM',
  ];

  useEffect(() => {
    changeGridHeight((filterRowData || []).length);
  }, [type, filterRowData]);

  const changeGridHeight = useEvent((rowCount = 0) => {
    const rowHeight = 37;
    const defHeight = type === 'DAILY_REPORT' ? 250 : 422;
    if (type === 'SCHD') {
      setGridHeight(defHeight);
    }
    if (rowCount < 1) {
      setGridHeight(defHeight);
    }
    setGridHeight(Math.max(rowHeight * (rowCount + 1), defHeight));
  });

  const onBeginningEdit = useEvent((grid, e) => {
    const binding = grid.columns[e.col].binding;
    // 현황
    if (binding.endsWith('_PROG_RT')) {
      const item = grid.rows[e.row].dataItem;
      // 완료된 경우 실적 변경 불가
      const cpltYnKey = binding.replace('_PROG_RT', '_CPLT_YN');
      if (cpltYnKey && item?.[cpltYnKey] === 'Y') {
        e.cancel = true;
      }
      // FAB-IN 실적입력 불가
      const cd = binding.match(/^'(.*)'_PROG_RT$/)?.[1];
      if (cd === 'T040') {
        e.cancel = true;
      }
    }
  });

  const onCellEditEnding = useEvent((grid, e) => {
    const binding = grid.columns[e.col].binding;
    // 현황
    if (binding.endsWith('_PROG_RT')) {
      const newVal = Number(grid.activeEditor?.value);
      if (Number.isNaN(newVal)) {
        e.cancel = true;
      }
      if (newVal < 0 || newVal > 100) {
        openMessageBar({
          type: 'error',
          content: t(
            'setup.label.단계별 실적은 0 ~ 100 사이의 숫자 값을 입력 하세요.',
            '단계별 실적은 0 ~ 100 사이의 숫자 값을 입력 하세요.'
          ),
        });
        e.cancel = true;
      }
      const cd = binding.match(/^'(.*)'_PROG_RT$/)?.[1];
      // FOB 실적 100 입력 불가
      if (cd === 'T010' && newVal == 100) {
        openMessageBar({
          type: 'error',
          content: t(
            'setup.label.FOB 완료처리는 하역장 도착처리나 Box 정보조회에서<br/>도착완료 처리시 자동실적 완료되기 때문에 실적 100를 넣을 수 없습니다.',
            'FOB 완료처리는 하역장 도착처리나 Box 정보조회에서<br/>도착완료 처리시 자동실적 완료되기 때문에 실적 100를 넣을 수 없습니다.'
          ),
        });
        e.cancel = true;
      }
    }
  });

  const onCellEditEnded = (grid, e) => {
    const binding = grid.columns[e.col].binding;
    // 현황
    if (binding.endsWith('_PROG_RT')) {
      const data = grid.rows[e.row].dataItem;
      const newVal = data[binding];
      const val = newVal ? Math.trunc(Number(newVal) * 10) / 10 : '';
      data[binding] = val;
    }
  };

  return (
    <>
      <div css={tabs.wrap}>
        <Tabs
          value={currentTabIndex}
          onChange={(e, newValue) => setCurrentTabIndex(newValue)}
          css={tabs.tab}
        >
          {tabMenus.map((o, idx) => (
            <Tab
              key={o.id}
              label={o.name}
              className={(rowData?.[o.id] || []).length > 0 ? 'tab-bg' : ''} // [26.01.27] tab 배경색 추가 (데이터 있는 경우 미리 탭으로 구분하기 위함)
              id={`setup-schedule-detail-tab-${idx}`}
              aria-controls={`setup-schedule-detail-tabpanel-${idx}`}
            />
          ))}
          {type === 'DAILY_REPORT' && (
            <SubTitleGroup>
              <div className="info warning">
                {t(
                  'setup.label.Fab-In 실적은 일보가 아닌 Box Fab-in처리로 반영됩니다.',
                  'Fab-In 실적은 일보가 아닌 Box Fab-in처리로 반영됩니다.'
                )}
              </div>
            </SubTitleGroup>
          )}
        </Tabs>
        <div css={tabs.btn}>
          <StatusDot style={{ marginRight: '10px' }}>
            <span className="done">{t('setup.label.시작전', '시작전')}</span>
            <span className="yellow">{t('setup.label.지연(30▼)', '지연(30▼)')}</span>
            <span className="red">{t('setup.label.지연(30▲)', '지연(30▲)')}</span>
            <span className="blue">{t('setup.label.정상', '정상')}</span>
            <span className="green">{t('setup.label.완료', '완료')}</span>
          </StatusDot>
          <div style={{ display: 'inline-flex', paddingRight: '10px', paddingTop: '7px' }}>
            <label htmlFor="delayYn" style={{ paddingTop: '0px' }}>
              {t('setup.label.지연여부', '지연여부')}
            </label>
            <Checkbox
              id="delayYn"
              css={st.checkbox}
              className="custom"
              checked={condition?.delayYn === 'Y'}
              onChange={(e, checked) => {
                setCondition((prev) => ({
                  ...prev,
                  delayYn: checked ? 'Y' : 'N',
                }));
              }}
              disableRipple
            />
          </div>
          {type === 'DAILY_REPORT' && (
            <>
              <div style={{ display: 'inline-flex', paddingRight: '10px', paddingTop: '7px' }}>
                <label htmlFor="changeYn" style={{ paddingTop: '0px' }}>
                  {t('setup.label.변경내용만 보기', '변경내용만 보기')}
                </label>
                <Checkbox
                  id="changeYn"
                  css={st.checkbox}
                  className="custom"
                  checked={condition?.changeYn === 'Y'}
                  onChange={(e, checked) => {
                    setCondition((prev) => ({
                      ...prev,
                      changeYn: checked ? 'Y' : 'N',
                    }));
                  }}
                  disableRipple
                />
              </div>
              {props?.showReloadResult && props?.onReloadResult && (
                <Button
                  css={IconButton.button}
                  className="reception"
                  onClick={props.onReloadResult}
                  disableRipple
                >
                  {t('setup.label.실적불러오기', '실적불러오기')}
                </Button>
              )}
              {props?.showTemplateDownload && props?.onTemplateDownload && (
                <Button
                  css={IconButton.button}
                  className="Exceldown"
                  onClick={props.onTemplateDownload}
                  disableRipple
                >
                  {t('com.button.템플릿 다운로드', '템플릿 다운로드')}
                </Button>
              )}
              {props?.showExcelUpload && props?.onExcelUploadCallback && (
                <ExcelUploadButton
                  onPreCheck={async () => {
                    // 작업일보 상세 > 기준일자가 필수값라서 onPreCheck 함수 없는 경우 false
                    if (!props?.onExcelUploadPreCheck) {
                      return false;
                    }
                    return props?.onExcelUploadPreCheck();
                  }}
                  onCallback={props.onExcelUploadCallback}
                />
              )}
            </>
          )}
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '화면 다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => handleSearch(condition)}
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
          <GridSettingButtons
            gridId={`schdStatDetailGrid_${type}`}
            layoutDefinition={layoutDefinition}
            changeShownColumns={(result) => {
              gridUiRef.current?.changeColumn?.(result);
              setIsChangeColumn(true);
            }}
          />
        </div>
      </div>
      <TabPanel value={currentTabIndex} index={currentTabIndex}>
        <CustomGrid
          id={`schdStatDetailGrid_${type}`}
          ref={gridUiRef}
          layoutDefinition={layoutDefinition}
          rowData={filterRowData}
          height={gridHeight}
          frozenColumns={7}
          isSelector={false}
          allowSorting={false}
          allowDragging={false}
          includeFilter={includePin}
          includePin={includePin}
          initialized={onInitialized}
          beginningEdit={onBeginningEdit}
          cellEditEnding={onCellEditEnding}
          cellEditEnded={onCellEditEnded}
          onFilterChange={(s, e) => {
            changeGridHeight(s.grid.rows.length);
          }}
        />
      </TabPanel>

      {type === 'SCHD' && (
        <GlobalBtnGroup>
          {(ensolYn === 'N' || hasPrjMgrAuth) && (
            <Button
              css={IconButton.button}
              className="icoNone primaryBtn"
              onClick={async () => {
                if (condition?.elmPrjId) {
                  if (ensolYn === 'N') {
                    if (!condition?.excpId) {
                      openMessageBar({
                        type: 'warning',
                        content: t(
                          'setup.label.협력업체정보가 없습니다.',
                          '협력업체정보가 없습니다.'
                        ),
                      });
                      return;
                    }
                    const res = await createRequestSetupResultRequest(
                      condition?.elmPrjId,
                      condition?.excpId
                    );
                    if (res?.successOrNot === 'N') {
                      openMessageBar({
                        type: 'error',
                        content: t(
                          'setup.label.실적완료요청 정보 갱신 실패했습니다.',
                          '실적완료요청 정보 갱신 실패했습니다.'
                        ),
                      });
                    }
                  }
                  setResultRequestCondition({
                    elmPrjId: condition?.elmPrjId,
                    excpId: condition?.excpId,
                  });
                  setOpenResultRequest(true);
                } else {
                  openMessageBar({
                    type: 'warning',
                    content: t(
                      'setup.label.프로젝트를 선택해 주세요.',
                      '프로젝트를 선택해 주세요.'
                    ),
                  });
                }
              }}
            >
              {ensolYn === 'Y'
                ? t('setup.label.실적 완료 승인', '실적 완료 승인')
                : t('setup.label.실적 완료 요청', '실적 완료 요청')}
            </Button>
          )}
        </GlobalBtnGroup>
      )}

      {isOpenScheduleChart && (
        <SetupEquipmentScheduleChartPopup
          open={isOpenScheduleChart}
          close={() => setOpenScheduleChart(false)}
          condition={{
            type: 'DTAL',
            elmPrjId: scheduleChartCondition?.elmPrjId,
            tgtEqpId: scheduleChartCondition?.tgtEqpId,
            targetData: scheduleChartCondition?.targetData,
          }}
        />
      )}

      {isOpenResultRequest && (
        <SetupResultCompleteRequestPopup
          open={isOpenResultRequest}
          close={() => {
            handleSearch(condition);
            setOpenResultRequest(false);
          }}
          condition={{
            elmPrjId: resultRequestCondition?.elmPrjId,
            excpId: resultRequestCondition?.excpId,
          }}
        />
      )}

      {openIssuePopup && (
        <ProjectIssueCreatePage
          open={openIssuePopup}
          close={() => {
            setOpenIssuePopup(false);
          }}
          onSearch={() => undefined}
          mode={PageMode.READ}
          isuId={issuePopupCondition?.isuId || ''}
        />
      )}
    </>
  );
};

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function TabPanel(props: { [x: string]: any; children: any; value: any; index: any }) {
  const { children, value, index } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`setup-schedule-detail-tabpanel-${index}`}>
      {value === index && <div>{children}</div>}
    </div>
  );
}

export default React.forwardRef(ScheduleStatusDetailContent);
